import axios from '@/lib/axios'
import { useUserStore } from '@/stores/user'
import { UseQueryOptions, useMutation, useQuery } from '@tanstack/vue-query'
import { helpers, required, requiredIf } from '@vuelidate/validators'
import { get } from '@vueuse/core'
import { create, omit, update } from 'lodash'
import { storeToRefs } from 'pinia'
import { MaybeRef, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouteLocationRaw, Router, useRoute, useRouter } from 'vue-router'
import { ToastInterface, useToast } from 'vue-toastification'

import type { User } from '../interfaces/user'
import { refetchAfterTick } from './refetchAfterTick'
import { resolveUserId } from './resolveUserId'

interface Props {
	immediate?: boolean
	params?: Params
	id?: string
	context?: string
	queryOptions?: UseQueryOptions
}

interface Params extends Omit<Partial<User>, 'id' | 'supervisorId'> {
	id?: MaybeRef<string | number>
	roleId?: string | number
	includes?: string[]
	// null explícito = quitar supervisor (el backend lo confirma: PUT con
	// supervisorId: null limpia el campo). undefined se cae del JSON y el
	// backend lo interpreta como "sin cambios", dejando el anterior intacto.
	supervisorId?: number | null
}

interface RedirectPayload {
	params: CreateParams
	redirect?: RouteLocationRaw
}

export type CreateParams = {
	[K in keyof User]?: K extends (
		| 'name'
		| 'lastname'
		| 'birthDate'
		| 'email'
		| 'role_id'
		| 'jobTitle'
		| 'company_id'
	) ?
		User[K]
	:	User[K] | undefined
}

const getUser = async (
	id: MaybeRef<string | number>,
	params: Params
): Promise<User> => {
	const omitParams = omit(params, ['id', 'includes'])

	const { data } = await axios.get<User>(`/api/users/${get(id)}`, {
		params: omitParams,
	})

	return data
}

export const createUser = async (
	params: CreateParams,
	toast: ToastInterface,
	router: Router,
	redirect?: RouteLocationRaw
): Promise<User | void> => {
	try {
		const { data } = await axios.post<User>(`/api/users`, params)

		if (redirect) {
			router.push({ name: 'users.index' })
		}

		toast.success('Usuario creado con éxito')
		return data
	} catch (err: any) {
		toast.error(err.response.data.message)
	}
}

const updateUser = async (
	params: Partial<Params>,
	toast: ToastInterface
): Promise<User> => {
	const { id } = params
	// No atrapar el error aquí: los callers de updateUserAsync tienen su propio
	// catch para mostrar el mensaje real del backend (p. ej. "El campo correo
	// electrónico ya ha sido registrado."). Tragarlo aquí con un toast genérico
	// lo dejaba invisible.
	const { data } = await axios.put(`/api/users/${id}`, params)

	toast.success('Usuario actualizado con éxito')
	return data
}

const useUser = (props: Props) => {
	const toast = useToast()
	const router = useRouter()
	// Sin desestructurar: `const { params } = useRoute()` congela los params en el valor
	// que tenían al montar. Con la ruta entera, el watch de más abajo sí ve el cambio.
	const route = useRoute()
	const { immediate = true, params = {} } = props
	const { user, createForm } = storeToRefs(useUserStore())
	const { setUser, setCreateFormValue, resetCreateForm } = useUserStore()

	const createMutation = useMutation(({ params, redirect }: RedirectPayload) =>
		createUser(params, toast, router, redirect)
	)
	const updateMutation = useMutation((params: Partial<Params>) =>
		updateUser(params, toast)
	)

	onMounted(() => resetCreateForm())

	/**
	 * Fecha límite para ser mayor de edad, como `YYYY-MM-DD` en hora LOCAL.
	 *
	 * Se arma con las partes locales y no con `toISOString()` porque este último
	 * pasa a UTC y puede correr el día, justo en el borde donde importa. Es la
	 * misma cuenta que hace el `max-date` del datepicker en UserForm, así que el
	 * calendario y la validación no se contradicen.
	 */
	const fechaLimiteMayoriaEdad = () => {
		const hoy = new Date()
		const limite = new Date(
			hoy.getFullYear() - 18,
			hoy.getMonth(),
			hoy.getDate()
		)
		const dosDigitos = (n: number) => String(n).padStart(2, '0')

		return `${limite.getFullYear()}-${dosDigitos(limite.getMonth() + 1)}-${dosDigitos(limite.getDate())}`
	}

	const rulesUserValidation = {
		name: {
			required: helpers.withMessage('El nombre es requerido', required),
		},
		lastname: {
			required: helpers.withMessage('El apellido es requerido', required),
		},

		gender: {
			required: helpers.withMessage('El género es requerido', required),
		},
		email: {
			required: helpers.withMessage(
				'El correo es requerido',
				requiredIf(() => {
					return createForm.value.haveAccess || createForm.value.companyId === 0
				})
			),
		},
		// El backend ya rechaza a los menores (`before:-18 years`), pero su
		// mensaje llega como "debe ser una fecha anterior a -18 years": el
		// parámetro crudo de la regla, que no le dice nada a quien da el alta.
		// Validarlo también acá evita el viaje al servidor y deja un mensaje
		// legible.
		birthDate: {
			mayorDeEdad: helpers.withMessage(
				'El viajero debe ser mayor de 18 años.',
				(value: unknown) => {
					// La fecha es opcional; vacía no es inválida.
					if (!value) return true

					const fecha = String(value).slice(0, 10)

					// Formato raro: que lo juzgue el backend, no inventamos un error.
					if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return true

					// Comparación de cadenas ISO: es correcta lexicográficamente.
					return fecha <= fechaLimiteMayoriaEdad()
				}
			),
		},
		// roleId: {
		//   required: helpers.withMessage("El rol es requerido", required),
		// },
	}

	// Sigue siendo un ref ESCRIBIBLE y no un computed: hay pantallas que le asignan a mano
	// —AvailabilityForm le pone el miembro del CEF, y la edición de usuario lo reapunta al
	// supervisor— y luego llaman a refetch(). Un computed las rompe.
	const userId = ref(resolveUserId(props.id, params.id, route.params.id))

	// Pero además tiene que SEGUIR a la ruta. <router-view> no lleva `:key`, así que ir de
	// un usuario a otro reusa el componente y `setup()` no vuelve a correr: sin esto, el ref
	// se queda con el id del primer usuario que cargó y la pantalla nunca cambia.
	// Para quien fija un id (props.id o params.id) esto es inofensivo: la precedencia de
	// resolveUserId devuelve el suyo y el valor no se mueve.
	watch(
		[() => route.params.id, () => get(params.id)],
		([routeId, paramsId]) => {
			userId.value = resolveUserId(props.id, paramsId, routeId)
		}
	)

	// El ref va DENTRO de la llave, no resuelto de antemano: vue-query desenvuelve refs en
	// las llaves y vuelve a consultar cuando cambian. Eso es lo que hace que cambiar de
	// usuario traiga al usuario correcto, y que cada uno tenga su propia entrada de caché.
	const {
		data,
		isLoading,
		isFetching,
		refetch: refetchQuery,
	} = useQuery<User>(
		['user', props.context, userId],
		() => getUser(userId.value, params),
		{
			retry: false,
			enabled: immediate,
			...(props.queryOptions as UseQueryOptions<User>),
		}
	)

	const refetch = refetchAfterTick(refetchQuery)

	watch(
		data,
		(value) => {
			if (value && props.context !== 'supervisor') {
				setUser(value)
			}
		},
		{ immediate: true }
	)

	onBeforeUnmount(() => resetCreateForm())

	return {
		data,
		user,
		isLoading,
		isFetching,
		refetch,
		createForm,
		createMutation,
		userId,

		rulesUserValidation,

		setCreateFormValue,
		resetCreateForm,

		createUserMutation: createMutation,
		createUser: createMutation.mutate,
		createUserAsync: createMutation.mutateAsync,
		createUserLoading: createMutation.isLoading,
		createUserError: createMutation.isError,

		updateUserMutation: updateMutation,
		updateUser: updateMutation.mutate,
		updateUserAsync: updateMutation.mutateAsync,
		updateUserLoading: updateMutation.isLoading,
	}
}

export default useUser
